<template>
  <div v-if="showMagMutualPanel"
       class="mag-mutual-panel">
    <div class="mag-mutual-panel__container">
      <div class="mag-mutual-panel__header">
        <div class="mag-mutual-panel__header-top">
          <img src="/search/mag-mutual-logo.svg" alt="MagMutual Logo">
          <utd-button :aria-label="`${isPanelExpanded ? 'collapse' : 'expand'} panel`"
                      class="mag-mutual-panel__toggle-button"
                      :class="iconClass" 
                      @click="togglePanel" />
        </div>
        <p v-if="isPanelExpanded" class="mag-mutual-panel__header-text">
          {{ C_MAG_MUTUAL.INFO }}
        </p>
        <p v-else class="mag-mutual-panel__show-content">
          {{ C_MAG_MUTUAL.COLLAPSED_TITLE }}
        </p>
      </div>

      <div v-show="isPanelExpanded" class="mag-mutual-panel__disclaimer">
        <p class="mag-mutual-panel__disclaimer-text">
          <strong>{{ C_MAG_MUTUAL.DISCLAIMER_BOLDED }}</strong>
          {{ C_MAG_MUTUAL.DISCLAIMER }}
        </p>
      </div>

      <div v-show="isPanelExpanded" class="mag-mutual-panel__content">
        <h3>{{ C_MAG_MUTUAL.MITIGATE_YOUR_RISK }}</h3>
        <p>{{ C_MAG_MUTUAL.DESCRIPTION }}</p>
        <ul>
          <li v-for="(item, index) in magMutualDataResponseList" :key="index">
            <utd-button class="mag-mutual-panel__risk-factor utd-button-style-text"
                        @click="(e) => openModal(item, e, index)">
              <span class="risk-factor" tabindex="0">{{ item.riskFactor }}</span>
            </utd-button>
          </li>
        </ul>
      </div>
    </div>

    <mag-mutual-modal ref="magMutualModalRef" @close-mag-mutual-modal="closeModal">
      <template #risk-factor-header>
        <span class="risk-factor-header">
          {{ selectedItem?.riskFactor }}
        </span>
      </template>
      <template #risk-factor-causes>
        <ul>
          <li v-for="(cause, idx) in selectedItem?.riskFactorCauses"
              :key="idx"
              class="risk-factor-cause">
            {{ cause }}
          </li>
        </ul>
      </template>
      <template #risk-factor-paragraphs>
        <div v-for="(paragraph, idx) in selectedItem?.riskFactorParagraphs"
             :key="idx">
          <p class="risk-factor-paragraph">
            <strong> {{ paragraph?.boldText }}</strong>{{ paragraph?.plainText }}
          </p>
        </div>
      </template>
    </mag-mutual-modal>
  </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { C_MAG_MUTUAL } from '_acaSrc/utility/constants';
import MagMutualModal from 
    '_acaSrc/components/contents/search/featuredResults/customPanel/MagMutualModal.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';

const store = useStore();

const magMutualDataStatus = computed(() => 
    store.getters['search/magMutualData']?.status || []);

const magMutualDataResponseList = computed(() => 
    store.getters['search/magMutualData']?.data?.riskFactorResponseList || []);

const showMagMutualPanel = computed(() => 
    magMutualDataStatus.value === 'success' && magMutualDataResponseList.value.length > 0);

const preference = computed(() => 
    store.getters['search/magMutualSearchPanelPreference']);

const isDesktop = computed(() => store.getters['device/isDesktopView']);
const isPanelExpanded = ref(false);

watch(
    () => preference.value,
    (newPref) => {
        if (newPref) {
            isPanelExpanded.value = isDesktop.value && newPref === 'expanded';
        }
    },
    { immediate: true }
);

const iconClass = computed(() => {
    return isPanelExpanded.value ? 'wkce-icon-contract-angle' : 'wkce-icon-expand-angle';
});

const magMutualModalRef = ref(null);
const selectedItem = ref(null);
const magMutualSearchPanelPreference = (value) => 
    store.dispatch('search/setMagMutualSearchPanelPreference', value);

function openModal(item, event, idx) {
    selectedItem.value = item;
    logUiClickEvent('riskFactor', `${item.riskFactor}_${idx+1}`);
    magMutualModalRef.value?.open(event);
}

function closeModal() {
    selectedItem.value = null;
}

function togglePanel() {
    isPanelExpanded.value = !isPanelExpanded.value;
    const panelState = isPanelExpanded.value ? 'expanded' : 'collapsed';
    logUiClickEvent('panelHeader', panelState);

    if (isDesktop.value) {
        magMutualSearchPanelPreference(panelState);
    }
}

function logUiClickEvent(element, opt) {
    logUiClickEventHelper({
        uiElementName: `MagMutual_${element}`,
        optData: opt
    });
}
</script>


<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.mag-mutual-panel {
  display: flex;
  justify-content: center;
  background-color: @WKCE-GRAY-TINT5;
  border-top: @DS1-UTD-GRAY-SINGLE-BORDER;
  border-bottom: @DS1-UTD-GRAY-SINGLE-BORDER;
}

.mag-mutual-panel__logo >img {
  width: 145px;
  height: 22px;
}

.mag-mutual-panel__container {
  .ds1-ma-2();
  background-color: @WKCE-GRAY-TINT1;
  border: @DS1-UTD-GRAY-SINGLE-BORDER;
  width: 100%;
  max-width: 450px;
}

p, h3 {
  .ds1-ma-0();
}

.mag-mutual-panel__header {
  .ds1-pa-2();
  border-bottom: 3px solid @WKCE-GREEN;
}

.mag-mutual-panel__show-content {
  .ds1-utd-weight-500();
  color: @DS1-UTD-PRIMARY-BG-COLOR;
  font-size: 14px;
}

.mag-mutual-panel__header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mag-mutual-panel__header-text, .mag-mutual-panel__disclaimer-text {
  font-size: 11px;
  line-height: 14px;
  color: @WKCE-ORANGE-SHADE4;
}

.mag-mutual-panel__header-text {
  font-style: italic;
  color: @DS1-UTD-PRIMARY-BG-COLOR;
}

.mag-mutual-panel__toggle-button {
  .ds1-pa-0();
  .ds1-ma-0();
  font-size: 22px;
  background-color: inherit;
  border: none;

  &::before {
    color: @DS1-UTD-PRIMARY-BG-COLOR;
  }
}

.mag-mutual-panel__disclaimer {
  .ds1-pa-2();
  background-color: @DS1-UTD-GRAY-BG-COLOR;
  border-bottom: 1px solid @DS1-UTD-GRAY-BORDER-COLOR;
}

.mag-mutual-panel__content {
  .ds1-pa-2();
  background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  border-top: 0;

  >h3 {
    color: @DS1-UTD-GRAY-TEXT-COLOR;
    font-size: 20px;
    font-weight: bold;
  }

  >p {
    .ds1-mt-1();
    .ds1-utd-size-2();
    .ds1-utd-weight-500();
    font-size: 14px;
    color: @DS1-UTD-GRAY-TEXT-COLOR;
  }

  #searchresults & >ul {
    .ds1-pl-2();
    .ds1-mt-2();
    .ds1-mb-0();
    display: block;
    list-style: none;

    .isDesktop & {
      .ds1-pl-1();
    }
  }

  li {
    .ds1-pb-0();
    .ds1-mt-1();
    .ds1-ml-0();
    position: relative;
    color: @WKCE-GREEN-SHADE2;
    height: fit-content;
    display: flex;
    align-items: center;
    gap: 0.4em;
    line-height: 20px;
    padding-left: 12px;
  
    &::before {
      content: '•';
      color: @WKCE-GREEN-SHADE2;
      position: absolute;
      left: 0;
      top: 0.1em;
    }

    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }

  li > * {
    display: inline-block;
    vertical-align: middle;
  }

  .risk-factor {
    .ds1-utd-size-2();
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
  }

  .risk-factor:focus {
    outline: 2px solid black;
  }
}

.mag-mutual-panel__risk-factor {
  .ds1-pl-0();
  .ds1-utd-weight-600();
  color: @WKCE-GREEN-SHADE2;
  text-align: left;
  line-height: 22px;
  height: fit-content;

  &:hover {
    color: @WKCE-GREEN-SHADE2;
  }
}

:deep(.mag-mutual-panel__risk-factor) {
  outline: none
}

@media only screen and (min-width: 768px) {
  .isDesktop {
    .mag-mutual-panel {
      background-color: transparent;
      border: none;
      max-width: 450px;
    }

    .mag-mutual-panel__container {
      .ds1-ma-0();
      .ds1-mb-6();
      width: 450px;
    }

    .mag-mutual-panel__content li {
      .ds1-mt-0();
    }
  }
}

.left-column {
  .risk-factor-header {
    .ds1-pb-2();
    color: @DS1-UTD-HEADER-TEXT-COLOR;
    font-weight: 600;
    font-size: 14px;
  }

  >ul {
    padding-left: 20px;

    >li {
      .ds1-pl-compact();
    }
  }
}

.risk-factor-cause {
  color: @DS1-UTD-GRAY-TEXT-COLOR;
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 12px;
}

.risk-factor-cause:last-child {
  .ds1-mb-0();
}

.risk-factor-paragraph {
  .ds1-mb-2();
  color: @DS1-UTD-GRAY-TEXT-COLOR;
  font-size: 12px;
  font-weight: 400;

  .first-sentence {
    color: @WKCE-GRAY-SHADE1;
    font-weight: 700;
  }
}

@media screen and (min-width: 768px) {
  .risk-factor-header {
     font-size: 16px;
   }

  .risk-factor-cause {
      font-size: 14px;
    }
}

</style>